// Thin localforage wrapper that keeps duplicated base64 images out of
// IndexedDB. Image-carrying values are compacted on write (images swapped
// for { $ref: "<sha256>" } placeholders) and expanded on read so callers
// never see refs. A single top-level `_assets` map in IDB holds the
// canonical copy of every unique image, keyed by SHA-256 hash.
//
// Any key NOT in ASSET_KEYS is passed straight to localforage unchanged.
// That covers journal/outbox, sync-state, GST caches, app context, etc.
//
// Write order invariant: `_assets` is flushed BEFORE the referencing key
// so a crash between the two cannot leave a dangling ref.

import localforage from 'localforage';
import secureLogger from '../utils/secureLogger';
import { ASSETS_KEY, compactValue, expandValue } from '../utils/assetRefs';
import { isShapeCheckableKey, validateCollection } from '../utils/schemaGuard';
import {
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_BG,
} from '../constants/localKeys';

// Keys that may contain repeated images. Everything else bypasses the
// compact layer entirely (see passthrough branch in setItem/getItem).
const ASSET_KEYS = new Set([
  COMPANY_KEY,
  CLIENTS_KEY,
  PRODUCTS_KEY,
  INVOICES_KEY,
  INVOICE_DETAILS,
  CLIENT_FORM_KEY,
  PRODUCT_FORM_KEY,
  INVOICE_FORM_KEY,
  DEFAULT_INVOICE_BG,
  QUOTATIONS_KEY,
  QUOTATION_DETAILS,
  QUOTATION_FORM_KEY,
  DEFAULT_QUOTATION_BG,
]);

let assetsCache = null;
let assetsLoadingPromise = null;

async function loadAssets() {
  if (assetsCache) return assetsCache;
  if (!assetsLoadingPromise) {
    assetsLoadingPromise = (async () => {
      try {
        assetsCache = (await localforage.getItem(ASSETS_KEY)) || {};
      } catch (err) {
        secureLogger.warn('Failed to load _assets, starting fresh:', err);
        assetsCache = {};
      }
      return assetsCache;
    })();
  }
  return assetsLoadingPromise;
}

export function isAssetKey(key) {
  return ASSET_KEYS.has(key);
}

export async function getItem(key) {
  const raw = await localforage.getItem(key);
  if (raw == null || !ASSET_KEYS.has(key)) return raw;
  const assets = await loadAssets();
  const value = expandValue(raw, assets);

  // Shape-check domain arrays on read. Runs only on keys with known shapes
  // (invoices, quotations, clients, products + detail lists). Purely advisory
  // here — we never filter or reject; the log is what future-us uses to
  // diagnose a silent corruption before users report broken UI.
  if (isShapeCheckableKey(key)) {
    const report = validateCollection(key, value);
    if (report.checked && report.invalid > 0) {
      secureLogger.warn(
        `Shape violation on read: ${key} has ${report.invalid}/${report.total} invalid rows (${(report.ratio * 100).toFixed(1)}%)`,
        { samples: report.samples }
      );
    }
  }

  return value;
}

export async function setItem(key, value) {
  if (!ASSET_KEYS.has(key)) return localforage.setItem(key, value);

  const assets = await loadAssets();
  const { compact, newAssets } = await compactValue(value, assets);

  if (newAssets.size > 0) {
    for (const [hash, blob] of newAssets) assets[hash] = blob;
    await localforage.setItem(ASSETS_KEY, assets);
  }

  await localforage.setItem(key, compact);
  return value;
}

export async function removeItem(key) {
  return localforage.removeItem(key);
}

// For the cloud-sync upload path: read the COMPACT shape directly from IDB
// without hydrating. Callers ship this straight to Drive so the blob stays
// small end-to-end.
export async function getRawItem(key) {
  return localforage.getItem(key);
}

// Return the current _assets map (loading from IDB if not yet cached).
// Used by cloudSyncService to include assets in the upload blob.
export async function getAssetsMap() {
  return loadAssets();
}

// Union incoming assets into the local _assets IDB key. Used by
// applyCloudDataToLocal when a compact cloud blob arrives — any new
// hashes are added so refs inside freshly-written rows will resolve.
export async function mergeIncomingAssets(incoming) {
  if (!incoming || typeof incoming !== 'object') return;
  const hashes = Object.keys(incoming);
  if (hashes.length === 0) return;

  const assets = await loadAssets();
  let changed = false;
  for (const h of hashes) {
    if (!assets[h] && typeof incoming[h] === 'string') {
      assets[h] = incoming[h];
      changed = true;
    }
  }
  if (changed) await localforage.setItem(ASSETS_KEY, assets);
}

// Drop the in-memory cache. Call after destructive operations (user
// switch wipes IDB, full cloud restore) so the next read reloads from disk.
export function invalidateAssetsCache() {
  assetsCache = null;
  assetsLoadingPromise = null;
}

export { ASSETS_KEY };